import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, Wand2, FileText, Languages } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Barra de herramientas IA para el composer de correo.
 *
 * Llama a `/api/correo/ai/asistir` para corregir/reescribir/traducir el
 * borrador actual, con el estilo aprendido del usuario aplicado.
 *
 * Props:
 *   - body: string (texto actual del borrador)
 *   - onChange: (newBody: string) => void
 *   - testid?: string  (default: 'composer-ia-toolbar')
 */
export default function ComposerAiToolbar({ body, onChange, testid = 'composer-ia-toolbar' }) {
  const [aiTool, setAiTool] = useState({ loading: false, last: null });

  const aiAsistir = async (accion, opciones = {}) => {
    if (!body || body.trim().length < 5) {
      toast.error('Escribe primero el borrador.');
      return;
    }
    setAiTool({ loading: true, last: accion });
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/ai/asistir`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accion, borrador: body,
          idioma_destino: opciones.idioma_destino || null,
          tono: opciones.tono || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      onChange(data.resultado);
      const cambios = (data.cambios_clave || []).slice(0, 3).join(' · ');
      toast.success(`IA · ${accion}${cambios ? `: ${cambios}` : ''}${data.perfil_aplicado ? ' · estilo aplicado' : ''}`);
    } catch (e) {
      toast.error(`IA: ${e.message}`);
    } finally {
      setAiTool({ loading: false, last: null });
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      <Button
        size="sm" variant="ghost"
        onClick={() => aiAsistir('corregir')}
        disabled={aiTool.loading}
        title="Corregir ortografía/gramática"
        className="h-7 text-violet-700 hover:bg-violet-50"
        data-testid="ai-corregir"
      >
        {aiTool.loading && aiTool.last === 'corregir' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wand2 className="w-3.5 h-3.5 mr-1" /> Corregir</>}
      </Button>
      <Button
        size="sm" variant="ghost"
        onClick={() => aiAsistir('reescribir', { tono: 'formal' })}
        disabled={aiTool.loading}
        title="Reescribir en tono formal"
        className="h-7 text-violet-700 hover:bg-violet-50"
        data-testid="ai-reescribir"
      >
        {aiTool.loading && aiTool.last === 'reescribir' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileText className="w-3.5 h-3.5 mr-1" /> Reescribir</>}
      </Button>
      <Button
        size="sm" variant="ghost"
        onClick={() => {
          const idioma = window.prompt('Idioma destino (ej: en, ca, fr, pt):', 'en');
          if (idioma && idioma.trim()) aiAsistir('traducir', { idioma_destino: idioma.trim() });
        }}
        disabled={aiTool.loading}
        title="Traducir el borrador"
        className="h-7 text-violet-700 hover:bg-violet-50"
        data-testid="ai-traducir"
      >
        {aiTool.loading && aiTool.last === 'traducir' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Languages className="w-3.5 h-3.5 mr-1" /> Traducir</>}
      </Button>
    </div>
  );
}
